import { lazy } from 'react';

import { Navigate, useParams } from 'react-router-dom';

import { config } from '@tankef/env';

import { type RoutePageMeta } from '../layouts/DashboardLayout/page-meta';

import type { RouteObject } from 'react-router-dom';

/**
 * Redirect del deep link in-app del BE (TandaInvitationMailer#in_app_invitation):
 *   /tandas/{tanda_group_id}/invitations/pending
 * El BE manda esta URL cuando el invitee YA tiene cuenta. El FE no expone
 * una vista de "invitations pending" — lleva al detalle de la tanda, donde
 * el invitee verá el banner correspondiente.
 */
function TandaInvitationsPendingRedirect() {
  const { id } = useParams<{ id: string }>();
  if (!id) return <Navigate to="/tandas" replace />;
  return <Navigate to={`/tandas/${encodeURIComponent(id)}`} replace />;
}

const ProductsCatalogPage = lazy(() =>
  import('@/domains/catalog/products/routes').then(m => ({ default: m.ProductsCatalogPage }))
);
const ProductApplicationPage = lazy(() =>
  import('@/domains/catalog/products/routes').then(m => ({ default: m.ProductApplicationPage }))
);
const InvestmentsPage = lazy(() =>
  import('@/domains/investment/routes').then(m => ({ default: m.InvestmentsPage }))
);
const InvestmentApplicationPage = lazy(() =>
  import('@/domains/investment/routes').then(m => ({ default: m.InvestmentApplicationPage }))
);
const InvestmentDetailPage = lazy(() =>
  import('@/domains/investment/routes').then(m => ({ default: m.InvestmentDetailPage }))
);
const CreditsPage = lazy(() =>
  import('@/domains/credit/routes').then(m => ({ default: m.CreditsPage }))
);
const CreditoModule = lazy(() =>
  import('@/domains/credit/routes').then(m => ({ default: m.CreditoModule }))
);
const CreditApplicationPage = lazy(() =>
  import('@/domains/credit/routes').then(m => ({ default: m.CreditApplicationPage }))
);
const CreditDetailPage = lazy(() =>
  import('@/domains/credit/routes').then(m => ({ default: m.CreditDetailPage }))
);
const SavingsBoxPage = lazy(() =>
  import('@/domains/savings/savings-box/routes').then(m => ({ default: m.SavingsBoxPage }))
);
const SavingsBoxApplicationPage = lazy(() =>
  import('@/domains/savings/savings-box/routes').then(m => ({
    default: m.SavingsBoxApplicationPage,
  }))
);
const SavingsBoxDetailPage = lazy(() =>
  import('@/domains/savings/savings-box/routes').then(m => ({ default: m.SavingsBoxDetailPage }))
);
const RotatingSavingsPage = lazy(() =>
  import('@/domains/savings/rotating-savings/routes').then(m => ({
    default: m.RotatingSavingsPage,
  }))
);
const RotatingSavingsApplicationPage = lazy(() =>
  import('@/domains/savings/rotating-savings/routes').then(m => ({
    default: m.RotatingSavingsApplicationPage,
  }))
);
const RotatingSavingsDetailPage = lazy(() =>
  import('@/domains/savings/rotating-savings/routes').then(m => ({
    default: m.RotatingSavingsDetailPage,
  }))
);
const MyTandasPage = lazy(() =>
  import('@/domains/savings/tandas/routes').then(m => ({ default: m.MyTandasPage }))
);
const TandasApplicationPage = lazy(() =>
  import('@/domains/savings/tandas/routes').then(m => ({ default: m.TandasApplicationPage }))
);
const TandasLobbyPage = lazy(() =>
  import('@/domains/savings/tandas/routes').then(m => ({ default: m.TandasLobbyPage }))
);
const TandasDetailPage = lazy(() =>
  import('@/domains/savings/tandas/routes').then(m => ({ default: m.TandasDetailPage }))
);
// TandasJoinPage vive en `public-routes.tsx` — la ruta `/tandas/join/:code`
// es pública por diseño (link compartido por WhatsApp/email a un usuario
// que puede no tener sesión todavía). Mantenerla protegida sacaba al
// usuario a /login antes de poder ver el preview.

const { products } = config;

// Page titles + breadcrumbs ride on each route's `handle` (plan 06 L3).
export function productRoutes(): RouteObject[] {
  return [
    {
      path: '/products',
      element: <ProductsCatalogPage />,
      handle: { titleKey: 'navigation.products', namespace: 'common' } satisfies RoutePageMeta,
    },
    {
      path: '/products/:category/:productId/apply',
      element: <ProductApplicationPage />,
      handle: {
        titleKey: 'productApply.title',
        namespace: 'common',
        breadcrumbs: [{ labelKey: 'navigation.products', namespace: 'common', href: '/products' }],
      } satisfies RoutePageMeta,
    },

    ...(products.investment !== 'disabled'
      ? [
          {
            path: '/investments',
            element: <InvestmentsPage />,
            handle: {
              titleKey: 'navigation.investments',
              namespace: 'common',
            } satisfies RoutePageMeta,
          },
          {
            path: '/investments/apply',
            element: <InvestmentApplicationPage />,
            handle: {
              // Sin `breadcrumbs` en el header: el flujo Contratar renderiza su
              // breadcrumb inline en el contenido (`<Breadcrumbs>`), mismo
              // patrón que tandas / caja de ahorro.
              titleKey: 'apply.title',
              namespace: 'common',
            } satisfies RoutePageMeta,
          },
          {
            path: '/investments/:id',
            element: <InvestmentDetailPage />,
            handle: {
              // Sin `breadcrumbs` en el header: el detalle renderiza su
              // breadcrumb inline en el contenido (`<Breadcrumbs>`). El título
              // dinámico lo fija `usePageHeader`.
              titleKey: 'detail.title',
              namespace: 'common',
            } satisfies RoutePageMeta,
          },
        ]
      : []),

    ...(products.credit !== 'disabled'
      ? [
          {
            path: '/credits',
            element: <CreditsPage />,
            handle: {
              titleKey: 'navigation.credits',
              namespace: 'common',
            } satisfies RoutePageMeta,
          },
          // /credits/module intentionally has no metadata — embedded UI sets its own.
          { path: '/credits/module', element: <CreditoModule /> },
          {
            path: '/credits/apply',
            element: <CreditApplicationPage />,
            handle: {
              titleKey: 'apply.title',
              namespace: 'common',
              breadcrumbs: [
                { labelKey: 'navigation.credits', namespace: 'common', href: '/credits' },
              ],
            } satisfies RoutePageMeta,
          },
          {
            path: '/credits/:id',
            element: <CreditDetailPage />,
            handle: {
              titleKey: 'detail.title',
              namespace: 'common',
              breadcrumbs: [
                { labelKey: 'navigation.credits', namespace: 'common', href: '/credits' },
              ],
            } satisfies RoutePageMeta,
          },
        ]
      : []),

    ...(products.savings_box !== 'disabled'
      ? [
          {
            path: '/savings-box',
            element: <SavingsBoxPage />,
            handle: {
              titleKey: 'navigation.savingsBox',
              namespace: 'common',
            } satisfies RoutePageMeta,
          },
          {
            path: '/savings-box/apply',
            element: <SavingsBoxApplicationPage />,
            handle: {
              // Sin `breadcrumbs` en el header: el flujo Contratar renderiza su
              // breadcrumb inline en el contenido (`<Breadcrumbs>`), mismo
              // patrón que tandas / inversiones.
              titleKey: 'apply.title',
              namespace: 'common',
            } satisfies RoutePageMeta,
          },
          {
            path: '/savings-box/:id',
            element: <SavingsBoxDetailPage />,
            handle: {
              // Sin `breadcrumbs` en el header: el detalle renderiza su
              // breadcrumb inline en el contenido (`<Breadcrumbs>`). El título
              // dinámico lo fija `usePageHeader`.
              titleKey: 'detail.title',
              namespace: 'common',
            } satisfies RoutePageMeta,
          },
        ]
      : []),

    ...(products.rotating_savings !== 'disabled'
      ? [
          {
            path: '/rotating-savings',
            element: <RotatingSavingsPage />,
            handle: {
              titleKey: 'navigation.rotatingSavings',
              namespace: 'common',
            } satisfies RoutePageMeta,
          },
          {
            path: '/rotating-savings/apply',
            element: <RotatingSavingsApplicationPage />,
            handle: {
              titleKey: 'apply.title',
              namespace: 'common',
              breadcrumbs: [
                {
                  labelKey: 'navigation.rotatingSavings',
                  namespace: 'common',
                  href: '/rotating-savings',
                },
              ],
            } satisfies RoutePageMeta,
          },
          {
            path: '/rotating-savings/:id',
            element: <RotatingSavingsDetailPage />,
            handle: {
              titleKey: 'detail.title',
              namespace: 'common',
              breadcrumbs: [
                {
                  labelKey: 'navigation.rotatingSavings',
                  namespace: 'common',
                  href: '/rotating-savings',
                },
              ],
            } satisfies RoutePageMeta,
          },
        ]
      : []),

    ...(products.tanda !== 'disabled'
      ? [
          {
            // /tandas usa MyTandasPage — diseño nuevo (mockup Cesar 2026-05-29)
            // con RoleChip + filter tabs + disabled CTAs según my-status caps.
            // Alinea con la convención de otros productos (/investments,
            // /credits, /savings-box — todos sin prefijo /mis-X).
            // TandasPage legacy queda exportada pero sin ruta — útil para
            // referencia hasta que se elimine en limpieza posterior.
            path: '/tandas',
            element: <MyTandasPage />,
            handle: {
              titleKey: 'navigation.tandas',
              namespace: 'common',
            } satisfies RoutePageMeta,
          },
          {
            path: '/tandas/apply',
            element: <TandasApplicationPage />,
            handle: {
              // Sin `breadcrumbs` en el header: el flujo Contratar renderiza
              // su propio breadcrumb inline (muted, estilo /tandas) dentro de
              // TandasModeSelector / TandasSimulatorForm. Ver MyTandasPage.
              titleKey: 'apply.title',
              namespace: 'common',
            } satisfies RoutePageMeta,
          },
          {
            path: '/tandas/:id/lobby',
            element: <TandasLobbyPage />,
            handle: {
              // Sin `breadcrumbs` en el header: el lobby renderiza su breadcrumb
              // inline en el contenido (`<Breadcrumbs>`), mismo patrón que el
              // resto de tandas. El título dinámico lo fija `usePageHeader`.
              titleKey: 'navigation.tandas',
              namespace: 'common',
            } satisfies RoutePageMeta,
          },
          // `/tandas/join/:code` se movió a `public-routes.tsx` (ruta
          // pública por diseño — link compartido sin sesión obligatoria).
          // Como `publicRoutes()` se prepende en `router.tsx`, gana en
          // orden de match a `/tandas/:id` aunque éste sea param.
          {
            // Compat con deep link in-app del BE (TandaInvitationMailer):
            // `/tandas/:id/invitations/pending` → `/tandas/:id` (detail).
            // Debe declararse ANTES de `/tandas/:id` para ganarle al match.
            path: '/tandas/:id/invitations/pending',
            element: <TandaInvitationsPendingRedirect />,
          },
          {
            path: '/tandas/:id',
            element: <TandasDetailPage />,
            handle: {
              // Sin `breadcrumbs` en el header: el detalle renderiza su
              // breadcrumb inline en el contenido (`<Breadcrumbs>`), mismo
              // patrón que el resto de tandas. El título dinámico (nombre de la
              // tanda) lo fija `usePageHeader`.
              titleKey: 'detail.title',
              namespace: 'common',
            } satisfies RoutePageMeta,
          },
        ]
      : []),
  ];
}
